import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { apiErrorMessage } from '@/api/apiError';
import { useListProtocolsQuery } from '@/api/hubApi';
import type { ProtocolStatus } from '@/api/protocolTypes';

const STATUS_COLOR: Record<ProtocolStatus, 'success' | 'default' | 'error' | 'info'> = {
    uploaded: 'default',
    processing: 'info',
    extracted: 'info',
    failed: 'error',
    approved: 'success',
};

export function ProtocolsPage() {
    const { data: protocols, isLoading, isFetching, error, refetch } = useListProtocolsQuery();

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h5">Protocols</Typography>
                {isFetching && <CircularProgress size={20} />}
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                >
                    {apiErrorMessage(error, 'Failed to load protocols')}
                </Alert>
            )}

            {isLoading ? (
                <CircularProgress />
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Title</TableCell>
                                <TableCell>Owner name</TableCell>
                                <TableCell>Owner ID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Agents scanned</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(protocols ?? []).map((protocol) => (
                                <TableRow key={protocol.id} hover>
                                    <TableCell>{protocol.title}</TableCell>
                                    <TableCell>
                                        {protocol.owner_name ?? '—'}{' '}
                                        <Typography
                                            component="span"
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            ({protocol.owner_type})
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>
                                        {protocol.owner_id}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={protocol.status}
                                            color={STATUS_COLOR[protocol.status]}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        {protocol.scanned_by_agents_count}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {protocols && protocols.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        No protocols yet.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
